import { CheckCircle2, FileText, Loader2, XCircle } from "lucide-react";
import { safeFormatDateTime } from "@/utils/formatDate";
import { useNavigate, useParams } from "react-router-dom";
import { useActionBarHeight } from "@/hooks/use-action-bar-height";
import { TopupBreakDown } from "@/features/topup/TopupBreakDown";
import { Button } from "@/components/ui/button";
import { useTopupConfirmation } from "@/features/topup/hook/useTopupConfirmation";
import { formatCurrency } from "@/utils/formatCurrency";
import { TopupConfirmationNotFound } from "./TopupConfimationNotFound";
import { ConfirmationProcessing } from "./TopupConfirmationProcessing";
import { useState } from "react";
import { toast } from "sonner";
import { handleDownloadTopupReceipt } from "@/hooks/downloadTopupReceipt";

export default function TopupConfimationPage() {
  const navigate = useNavigate();
  const { topupno } = useParams<{ topupno: string }>();

  const { loading, topupSummary, timedOut, notFound, checkStatus, checking } =
    useTopupConfirmation(topupno);

  const [downloadingReceipt, setDownloadingReceipt] = useState(false);

  // Expose the fixed action bar height so the layout's bottom-hero sits above it
  const footerRef = useActionBarHeight<HTMLElement>();

  const status = topupSummary?.paymentStatus;
  const isProcessing = loading || status === "pending";

  // No ticket no / booking cannot be found
  if (notFound) {
    return <TopupConfirmationNotFound />;
  }

  // Polling reached maximum attempts
  if (timedOut) {
    return (
      <ConfirmationProcessing
        status={status}
        timedOut
        onCheckStatus={checkStatus}
        checking={checking}
      />
    );
  }

  // Still waiting for payment confirmation
  if (isProcessing) {
    return <ConfirmationProcessing status={status} checking={checking} />;
  }

  const handleDownloadReceipt = async () => {
    if (!topupno || downloadingReceipt) return;

    try {
      setDownloadingReceipt(true);

      await handleDownloadTopupReceipt(topupno);
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : "Failed to download booking confirmation.",
      );
    } finally {
      setDownloadingReceipt(false);
    }
  };

  const handleReturn = () => {
    navigate("/top-up");
  };

  const isFailed = status === "fail";

  return (
    <div className="mx-auto max-w-7xl px-4 pt-4 pb-32 sm:px-6 lg:px-8 md:max-w-5xl md:pb-16">
      <div className="flex flex-col items-center text-center p-2">
        {isFailed ? (
          <XCircle size={48} className="text-destructive" aria-hidden="true" />
        ) : (
          <CheckCircle2
            size={48}
            className="text-green-700"
            aria-hidden="true"
          />
        )}
        <h1
          className={`mt-2 text-lg font-semibold ${
            isFailed ? "text-destructive" : "text-heading"
          }`}
        >
          {isFailed ? "Payment Failed" : "Payment Received Successfully"}
        </h1>
        <p className="mt-2 text-sm font-medium text-secondary-foreground">
          {isFailed
            ? "Your payment could not be completed."
            : "Added top-up tickets"}
        </p>
        <p className="mt-2 text-sm font-medium text-mute">
          {isFailed ? "No charge was made. Please try booking again." : ""}
        </p>
      </div>

      {!isFailed && (
        <>
          <h2 className="mt-8 text-lg font-semibold text-secondary-foreground">
            Summary
          </h2>
          <section className="mt-2 rounded-2xl bg-card px-4 py-6 shadow-xl">
            <div className="md:grid md:grid-cols-3 md:items-center md:gap-4">
              <div>
                <p className="text-xs font-medium text-muted">
                  Top-up Request no.
                </p>
                <p className="text-sm mt-1 font-medium text-secondary-foreground">
                  {topupSummary?.topUpNo}
                </p>
              </div>
              <div className="mt-4 md:mt-0">
                <p className="text-xs font-medium text-muted">
                  Purchase Date/Time
                </p>
                <p className="text-sm mt-1 font-medium text-secondary-foreground">
                  {safeFormatDateTime(topupSummary?.purchaseDateTime) ?? "-"}
                </p>
              </div>
            </div>
          </section>

          <h2 className="mt-8 text-lg font-semibold text-secondary-foreground">
            Payment Details
          </h2>
          <div className="mt-2 rounded-2xl bg-card px-4 py-6 shadow-xl">
            <div className="md:grid md:grid-cols-3 md:items-center md:gap-4">
              <div className="mt-0 flex items-start justify-between gap-4 md:contents">
                <div>
                  <p className="text-xs font-medium text-muted">
                    Payment Method
                  </p>
                  <p className="text-sm mt-1 font-medium text-secondary-foreground">
                    {topupSummary?.paymentMethod ?? "-"}
                  </p>
                </div>
              </div>
               <div className="mt-4 flex items-start justify-between gap-4 md:mt-0 md:contents">
                <div>
                  <p className="text-xs font-medium text-muted">
                    Total Amount
                  </p>
                  <p className="text-sm mt-1 font-medium text-secondary-foreground">
                    {formatCurrency(topupSummary?.totalAmount ?? 0)}
                  </p>
                </div>
                <div className="text-right md:text-left">
                  <p className="text-xs font-medium text-muted">Processing Fee</p>
                  <p className="text-sm mt-1 font-medium text-secondary-foreground">
                    {formatCurrency(topupSummary?.processingFee ?? 0)}
                  </p>
                </div>
              </div>
              <div className="mt-4 flex items-start justify-between gap-4 md:mt-0 md:contents">
                <div>
                  <p className="text-xs font-medium text-muted">
                    Commission Price
                  </p>
                  <p className="text-sm mt-1 font-medium text-secondary-foreground">
                    -{formatCurrency(topupSummary?.commissionPrice ?? 0)}
                  </p>
                </div>
                <div className="text-right md:text-left">
                  <p className="text-xs font-medium text-muted">Final Price</p>
                  <p className="text-sm mt-1 font-medium text-secondary-foreground">
                    {formatCurrency(topupSummary?.finalPrice ?? 0)}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <h2 className="mt-8 text-lg font-semibold text-secondary-foreground">
            Top-up Details
          </h2>
          <div className="mt-3 flex flex-col gap-6">
            {topupSummary?.routeList?.map((route) => (
              <TopupBreakDown key={route.routeGroup} route={route} />
            ))}
          </div>

          <div className="mt-8 flex flex-col gap-8 md:flex-row">
            <div className="flex-1">
              <Button
                type="button"
                variant="outline"
                className="w-full border-2 border-primary text-primary bg-card gap-2.5 hover:bg-primary-soft"
                onClick={handleDownloadReceipt}
                disabled={downloadingReceipt}
              >
                {downloadingReceipt ? (
                  <>
                    <Loader2 size={20} className="animate-spin" />
                    Downloading...
                  </>
                ) : (
                  <>
                    <FileText size={20} />
                    Download Receipt
                  </>
                )}
              </Button>
            </div>
            <div className="flex-1 hidden md:block">
              <Button
                type="button"
                variant="secondary"
                className="w-full cursor-pointer"
                onClick={handleReturn}
              >
                Return to Top-up
              </Button>
            </div>
          </div>

          <footer
            ref={footerRef}
            className="fixed inset-x-0 bottom-0 z-20 border-t border-border bg-card md:hidden"
          >
            <div className="mx-auto flex w-full max-w-7xl items-center justify-between gap-4 px-4 py-4 sm:px-6 lg:px-8">
              <Button
                type="button"
                variant="secondary"
                className="w-full cursor-pointer"
                onClick={handleReturn}
              >
                Return to Top-up
              </Button>
            </div>
          </footer>
        </>
      )}

      {isFailed && (
        <div className="flex flex-1 mt-5 items-center justify-center md:flex">
          <Button
            type="button"
            variant="secondary"
            className="w-full cursor-pointer md:w-lg"
            onClick={handleReturn}
          >
            Return to Top-up
          </Button>
        </div>
      )}
    </div>
  );
}
